/**
 *
 *  useBodyScrollLock
 *  A hook that enables body scroll locking (for mobile and desktop Safari/Chrome/Firefox)
 *  without breaking scrolling of a target element (eg. modal/lightbox/flyouts/nav-menus).
 *
 *  Created by Vladimir Shislyannikov on 01/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import {
  useState,
  useEffect,
  useRef,
  // useLayoutEffect,
  useCallback,
  useMemo,
} from "react";
import ReactDOM from "react-dom";
import { isIosDevice } from "../../utils";

// Helpers

/**
 * Older browsers don't support event options, feature detect it.
 * @private
 */

const supportsEventOptions = (() => {
  let result = false;

  if (typeof window !== "undefined") {
    const passiveTestOptions = {
      get passive() {
        result = true;

        return undefined;
      },
    };

    window.addEventListener("testPassive", null, passiveTestOptions);
    window.removeEventListener("testPassive", null, passiveTestOptions);
  }

  return result;
})();

export default function useBodyScrollLock(
  // ref.current,
  enabled,
  // props,
  options
) {
  const hasPassiveEvents = supportsEventOptions;
  const [bottomBarHeight, setBottomBarHeight] = useState(0);

  const _ref = useRef(null);
  const locksRef = useRef([]);
  const previousBodyPositionRef = useRef();
  const documentListenerAddedRef = useRef(false);
  const initialClientYRef = useRef(-1);
  const previousBodyOverflowSettingRef = useRef();
  const previousBodyPaddingRightRef = useRef();

  const refCallback = useCallback((node) => {
    _ref.current = node;
  }, []);

  /**
   * returns true if `el` should be allowed to receive touchmove events.
   *
   * @param {HTMLElement} el
   * @returns boolean
   * @private
   */
  const allowTouchMove = (el) =>
    locksRef.current.some((lock) => {
      if (
        lock.options &&
        lock.options.allowTouchMove &&
        lock.options.allowTouchMove(el)
      ) {
        return true;
      }

      return false;
    });

  /**
   * preventDefault callback that allows to multitouch events
   *
   * @param BodyScrollOptions options
   * @private
   */
  const preventDefault = useCallback((rawEvent) => {
    const e = rawEvent || window.event;

    // For the case whereby consumers adds a touchmove event listener to document.
    // Recall that we do document.addEventListener('touchmove', preventDefault, { passive: false })
    // in disableBodyScroll - so if we provide this opportunity to allowTouchMove, then
    // the touchmove event on document will break.
    if (allowTouchMove(e.target)) {
      return true;
    }

    // Do not prevent if the event has more than one touch (usually meaning this is a multi touch gesture like pinch to zoom).
    if (e.touches.length > 1) return true;

    if (e.preventDefault) e.preventDefault();

    return false;
  }, []);

  /**
   * Sets overflow: hidden on body and adjusts padding right
   *
   * @param BodyScrollOptions options
   * @private
   */
  const setOverflowHidden = () => {
    // If previousBodyPaddingRight is already set, don't set it again.
    if (previousBodyPaddingRightRef.current === undefined) {
      const reserveScrollBarGap =
        !!options && options.reserveScrollBarGap === true;
      const scrollBarGap =
        window.innerWidth - document.documentElement.clientWidth;

      if (reserveScrollBarGap && scrollBarGap > 0) {
        const computedBodyPaddingRight = parseInt(
          window
            .getComputedStyle(document.body)
            .getPropertyValue("padding-right"),
          10
        );

        previousBodyPaddingRightRef.current = document.body.style.paddingRight;
        document.body.style.paddingRight = `${
          computedBodyPaddingRight + scrollBarGap
        }px`;
      }
    }

    // If previousBodyOverflowSetting is already set, don't set it again.
    if (previousBodyOverflowSettingRef.current === undefined) {
      previousBodyOverflowSettingRef.current = document.body.style.overflow;

      document.body.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
    }
  };

  /**
   * @private
   */
  const restoreOverflowSetting = () => {
    if (previousBodyPaddingRightRef.current !== undefined) {
      document.body.style.paddingRight = previousBodyPaddingRightRef.current;

      // Restore previousBodyPaddingRight to undefined so setOverflowHidden knows it
      // can be set again.
      previousBodyPaddingRightRef.current = undefined;
    }

    if (previousBodyOverflowSettingRef.current !== undefined) {
      document.body.style.overflow = previousBodyOverflowSettingRef.current;

      // Restore previousBodyOverflowSetting to undefined
      // so setOverflowHidden knows it can be set again.
      previousBodyOverflowSettingRef.current = undefined;
    }
  };

  /**
   * @private
   */
  const setPositionFixed = useCallback(
    () =>
      window.requestAnimationFrame(() => {
        // If previousBodyPosition is already set, don't set it again.
        if (previousBodyPositionRef.current === undefined) {
          previousBodyPositionRef.current = {
            position: document.body.style.position,
            top: document.body.style.top,
            left: document.body.style.left,
          };

          // Update the dom inside an animation frame
          const { scrollY, scrollX, innerHeight } = window;

          document.body.style.position = "fixed";
          document.body.style.top = `${-scrollY}px`;
          document.body.style.left = `${-scrollX}px`;

          document.body.style.overflow = "hidden";
          document.body.style.WebkitOverflowScrolling = "none";

          // iOS 15 fix
          document.documentElement.style.height = "100vh";

          setTimeout(
            () =>
              window.requestAnimationFrame(() => {
                // this line prevents the content
                // from hiding behind the address bar
                // window.scrollTo(0, 1);

                // Attempt to check if the bottom bar appeared due to the position change
                const _bottomBarHeight = innerHeight - window.innerHeight;

                if (_bottomBarHeight && scrollY >= innerHeight) {
                  // Move the content further up so that the bottom bar doesn't hide it
                  document.body.style.top = -(scrollY + _bottomBarHeight);
                  setBottomBarHeight(_bottomBarHeight);
                }
              }),
            300
          );
        }
      }),
    []
  );

  /**
   * @private
   */
  const restorePositionSetting = useCallback(() => {
    if (previousBodyPositionRef.current !== undefined) {
      // Convert the position from "px" to Int
      const y = -parseInt(document.body.style.top, 10);
      const x = -parseInt(document.body.style.left, 10);

      // Restore styles
      document.body.style.position = previousBodyPositionRef.current.position;
      document.body.style.top = previousBodyPositionRef.current.top;
      document.body.style.left = previousBodyPositionRef.current.left;

      document.body.style.overflow = "";
      document.body.style.WebkitOverflowScrolling = "";

      // iOS 15 fix
      document.documentElement.style.height = "";

      // Restore scroll
      window.scrollTo(x, y);

      // setPreviousBodyPosition(undefined);
      previousBodyPositionRef.current = undefined;
    }
  }, []);

  /**
   * https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight#Problems_and_solutions
   * @private
   */
  const isTargetElementTotallyScrolled = () => {
    if (!_ref.current) {
      return false;
    }

    const targetElement = ReactDOM.findDOMNode(_ref.current);

    return (
      targetElement.scrollHeight - targetElement.scrollTop <=
      targetElement.clientHeight
    );
  };

  const handleScroll = useCallback(
    (event, targetElement) => {
      const clientY =
        event.targetTouches[0].clientY - initialClientYRef.current;

      if (allowTouchMove(event.target)) {
        return false;
      }

      if (targetElement && targetElement.scrollTop === 0 && clientY > 0) {
        // element is at the top of its scroll.
        return preventDefault(event);
      }

      if (isTargetElementTotallyScrolled() && clientY < 0) {
        // element is at the bottom of its scroll.
        return preventDefault(event);
      }

      event.stopPropagation();

      return true;
    },
    [preventDefault]
  );

  /**
   * returns true if `el` should be allowed to receive touchmove events.
   *
   * @private
   */
  const disableBodyScroll = () => {
    // targetElement must be provided
    if (!_ref || !_ref.current) {
      // eslint-disable-next-line no-console
      console.error(
        "disableBodyScroll unsuccessful - ref is not initialased when calling disableBodyScroll on IOS devices."
      );
      return;
    }

    const targetElement = ReactDOM.findDOMNode(_ref.current);

    // disableBodyScroll must not have been called on this targetElement before
    if (locksRef.current.some((lock) => lock.targetElement === targetElement)) {
      return;
    }

    const lock = {
      targetElement,
      options, // TODO: make separate options for each elment. or not???
    };

    locksRef.current = [...locksRef.current, lock];

    if (isIosDevice) {
      setPositionFixed();
    } else {
      setOverflowHidden(options);
    }

    if (isIosDevice) {
      targetElement.ontouchstart = (event) => {
        if (event.targetTouches.length === 1) {
          // detect single touch.
          initialClientYRef.current = event.targetTouches[0].clientY;
        }
      };

      targetElement.ontouchmove = (event) => {
        if (event.targetTouches.length === 1) {
          // detect single touch.
          handleScroll(event, targetElement);
        }
      };

      if (!documentListenerAddedRef.current) {
        document.addEventListener(
          "touchmove",
          preventDefault,
          hasPassiveEvents ? { passive: false } : undefined
        );

        documentListenerAddedRef.current = true;
      }
    }
  };

  /**
   * Clear all locks. For iOS devices also clears ontouchstart/ontouchmove handlers,
   * and the references.
   * @private
   */
  const clearAllBodyScrollLocks = () => {
    if (isIosDevice) {
      locksRef.current.forEach((lock) => {
        const targetElement = ReactDOM.findDOMNode(lock.targetElement);

        targetElement.ontouchstart = null;
        targetElement.ontouchmove = null;
      });

      if (documentListenerAddedRef.current) {
        document.removeEventListener(
          "touchmove",
          preventDefault,
          hasPassiveEvents ? { passive: false } : undefined
        );

        documentListenerAddedRef.current = false;
      }

      // Reset initial clientY.
      initialClientYRef.current = -1;
    }

    if (isIosDevice) {
      restorePositionSetting();
    } else {
      restoreOverflowSetting();
    }

    locksRef.current = [];
  };

  /**
   * enables back the ability to scroll a body
   * @private
   */
  const enableBodyScroll = () => {
    if (!_ref || !_ref.current) {
      // eslint-disable-next-line no-console
      console.error(
        "enableBodyScroll unsuccessful - ref must be initialased when calling enableBodyScroll on IOS devices."
      );

      return;
    }

    const targetElement = ReactDOM.findDOMNode(_ref.current);
    const activeLocks = locksRef.current.filter(
      (lock) => lock.targetElement !== targetElement
    );

    locksRef.current = activeLocks;

    if (isIosDevice) {
      targetElement.ontouchstart = null;
      targetElement.ontouchmove = null;

      if (documentListenerAddedRef.current && activeLocks.length === 0) {
        document.removeEventListener(
          "touchmove",
          preventDefault,
          hasPassiveEvents ? { passive: false } : undefined
        );

        documentListenerAddedRef.current = false;
      }
    }

    if (isIosDevice) {
      restorePositionSetting();
    } else {
      restoreOverflowSetting();
    }

    previousBodyPositionRef.current = undefined;
  };

  useEffect(() => {
    if (!_ref.current) {
      if (previousBodyPositionRef.current !== undefined) {
        clearAllBodyScrollLocks();
      }

      return;
    }

    if (enabled) {
      disableBodyScroll();
    } else {
      enableBodyScroll();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled /*clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll*/,
  ]);

  useEffect(
    () => () => clearAllBodyScrollLocks(),
    [
      // eslint-disable-line react-hooks/exhaustive-deps
      /*clearAllBodyScrollLocks*/
    ]
  );

  return useMemo(
    () => ({
      bottomBarHeight,
      refCallback,
      targetRef: _ref,
    }),
    [bottomBarHeight, refCallback, _ref]
  );
}
